import type { GridValidRowModel } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useState } from 'react';

interface UseInfiniteLoadOptions<T extends GridValidRowModel> {
    /**
     * All available rows to paginate through
     */
    allRows: T[];
    /**
     * Number of rows to load per page
     */
    pageSize: number;
    /**
     * Loading state to prevent multiple simultaneous loads
     */
    isLoading?: boolean;
}

interface UseInfiniteLoadReturn<T extends GridValidRowModel> {
    /**
     * Currently displayed rows
     */
    displayedRows: T[];
    /**
     * Whether more rows are available to load
     */
    hasMore: boolean;
    /**
     * Function to load the next batch of rows
     */
    loadMore: () => void;
    /**
     * Reset to initial page
     */
    reset: () => void;
}

export function useInfiniteLoad<T extends GridValidRowModel>({
    allRows,
    pageSize,
    isLoading = false
}: UseInfiniteLoadOptions<T>): UseInfiniteLoadReturn<T> {
    const [displayedRows, setDisplayedRows] = useState<T[]>([]);
    const [hasMore, setHasMore] = useState(true);

    const reset = useCallback(() => {
        const initialRows = allRows.slice(0, pageSize);
        setDisplayedRows(initialRows);
        setHasMore(allRows.length > pageSize);
    }, [allRows, pageSize]);

    useEffect(() => {
        reset();
    }, [reset]);

    const loadMore = useCallback(() => {
        if (!hasMore || isLoading) {
            return;
        }

        const currentLength = displayedRows.length;
        const nextRows = allRows.slice(0, currentLength + pageSize);

        setDisplayedRows(nextRows);
        setHasMore(nextRows.length < allRows.length);
    }, [displayedRows, allRows, pageSize, hasMore, isLoading]);

    return {
        displayedRows,
        hasMore,
        loadMore,
        reset
    };
}
